import React from "react";
import { motion } from "framer-motion";
import { Building2, Cpu } from "lucide-react";

const IndustrySection = ({ industries, technologies }) => {
  if (!industries?.length && !technologies?.length) return null;

  return (
    <section className="py-6 lg:py-8 ">
      <div className="max-w-[1700px] mx-auto px-6 sm:px-8 lg:px-10 xl:px-12">
        {/* Heading */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45 }}
          viewport={{ once: true }}
          className="mb-8"
        >
          {/* Badge */}
          <div className="inline-flex items-center rounded-full bg-gradient-to-r from-orange-100 via-pink-100 to-purple-100 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-orange-600">
            Industries & Technologies
          </div>

          {/* Title */}
          <h2 className="mt-4 text-2xl md:text-3xl lg:text-[34px] font-bold leading-tight text-gray-900">
            Industries We Serve & Technologies We Use
          </h2>

          {/* Divider */}
          <div className="mt-3 h-1 w-16 rounded-full bg-gradient-to-r from-[#FF7A18] via-[#FF5B8A] to-[#9C4DFF]" />

          {/* Description */}
          <p className="mt-4 max-w-5xl text-[15px] leading-7 text-gray-600">
            We build scalable AI solutions across industries using modern cloud
            platforms, enterprise AI technologies, and intelligent automation.
          </p>
        </motion.div>

        {/* Cards */}
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
          {/* Industries */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.45 }}
            viewport={{ once: true }}
            className="rounded-xl border border-gray-200 bg-white p-5 lg:p-6 shadow-sm hover:shadow-md transition-all duration-300"
          >
            <div className="mb-5 flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-r from-[#FF7A18] via-[#FF5B8A] to-[#9C4DFF]">
                <Building2 size={18} className="text-white" />
              </div>

              <div>
                <p className="text-[10px] uppercase tracking-[2px] text-gray-500">
                  Business Domains
                </p>

                <h3 className="text-lg font-semibold text-gray-900">
                  Industries
                </h3>
              </div>
            </div>

            <div className="flex flex-wrap gap-2.5">
              {industries?.map((industry, index) => (
                <motion.span
                  key={index}
                  whileHover={{ y: -2 }}
                  className="rounded-full border border-pink-100 bg-pink-50 px-3 py-1.5 text-sm font-medium text-gray-700 transition-all duration-300 hover:border-[#EC4899] hover:text-[#EC4899]"
                >
                  {industry}
                </motion.span>
              ))}
            </div>
          </motion.div>

          {/* Technologies */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.45 }}
            viewport={{ once: true }}
            className="rounded-xl bg-gradient-to-br from-[#FF7A18] via-[#FF5B8A] to-[#9C4DFF] p-5 lg:p-6 shadow-md"
          >
            <div className="mb-5 flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-white">
                <Cpu size={18} className="text-[#EC4899]" />
              </div>

              <div>
                <p className="text-[10px] uppercase tracking-[2px] text-white/70">
                  Technology Stack
                </p>

                <h3 className="text-lg font-semibold text-white">
                  Technologies
                </h3>
              </div>
            </div>

            <div className="flex flex-wrap gap-2.5">
              {technologies?.map((tech, index) => (
                <motion.span
                  key={index}
                  whileHover={{ y: -2 }}
                  className="rounded-full border border-white/20 bg-white/10 px-3 py-1.5 text-sm font-medium text-white backdrop-blur-sm transition-all duration-300 hover:bg-white hover:text-[#EC4899]"
                >
                  {tech}
                </motion.span>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default IndustrySection;